import { type RefObject, useEffect, useLayoutEffect } from 'react';

import {
  findFloatingOverlay,
  isInsideFloatingOverlay,
} from '@/lib/base/overlays/floating-overlay';

import { isPopoverOpenWithin } from './isPopoverOpenWithin';

/**
 * How long a picker popover's Escape/click-away close waits for focus to move
 * before deciding where it landed. react-aria hands focus back within a frame
 * or two of the exit animation; this only has to outlast that.
 */
const POPOVER_CLOSE_SETTLE_MS = 300;
/**
 * How long a freshly-opened picker popover must stay open before a
 * subsequent close is treated as a real dismissal. An auto-opened picker
 * restores focus to <body> and remounts (~300ms) before its menu is
 * focused; only after that has it genuinely "opened". Chosen to outlast
 * that remount+focus window; the focusin-inside-overlay signal below
 * usually marks the popover settled well before this fallback fires.
 */
const POPOVER_OPEN_SETTLE_MS = 500;
/**
 * How many times the open-settle fallback re-arms itself when the popover is
 * still open (`aria-expanded="true"`) but focus hasn't genuinely entered it
 * yet. A freshly created record's slower options fetch (schema still
 * resolving, network latency) can keep the popover in its opening/remount
 * window well past one `POPOVER_OPEN_SETTLE_MS` interval; declaring it
 * settled from that stale snapshot lets the very next close — still part of
 * the same opening cycle, not a real dismissal — read as the user closing
 * the picker and commit the row while it never visibly finished opening.
 * Capped so a popover that legitimately never receives focus (no focusable
 * content) still settles eventually instead of waiting forever.
 */
const MAX_OPEN_SETTLE_ATTEMPTS = 6;

/**
 * Whether focus has GENUINELY entered the popover opened within `container`
 * — not merely because `aria-expanded` flipped true. During the opening
 * window (popover open, focus not yet inside it) this is false, so callers
 * keep focus enforcement active rather than standing down early.
 */
function hasFocusEnteredPopover(
  container: HTMLElement,
  target: Node | null
): boolean {
  return (
    isPopoverOpenWithin(container) &&
    isInsideFloatingOverlay(target, { surface: container })
  );
}

/**
 * Like `hasFocusEnteredPopover`, but distinguishes THIS picker's popover from
 * an unrelated floating overlay that merely happens to hold focus while our
 * trigger is open. `hasFocusEnteredPopover` answers the looser "focus is in a
 * floating layer above `container`", so a different picker or a menu elsewhere
 * reads as our popover having settled — and when that seeds `settledOpen` at
 * install, a following opening/remount close commits the row before the user
 * dismisses or selects.
 *
 * react-aria links an open trigger to the overlay it controls via
 * `aria-controls` (the open popover's id). When that link resolves and focus
 * is positively inside a DIFFERENT floating overlay than the controlled one,
 * this returns false so the caller falls through to the settle-interval
 * fallback instead. Every ambiguous case — no link, an unresolved id, or focus
 * in the same overlay the trigger controls — keeps the looser positive answer,
 * so this is never stricter than the shipped guard where ownership can't be
 * confirmed (e.g. a native popover with no `aria-controls`).
 */
function hasFocusEnteredOwnPopover(
  container: HTMLElement,
  target: Node | null
): boolean {
  if (!hasFocusEnteredPopover(container, target)) {
    return false;
  }
  const openTrigger = container.querySelector('[aria-expanded="true"]');
  const controlsId = openTrigger?.getAttribute('aria-controls');
  if (!controlsId) {
    return true;
  }
  const controlled = container.ownerDocument.getElementById(controlsId);
  if (!controlled) {
    return true;
  }
  const ownOverlay = findFloatingOverlay(controlled);
  const focusedOverlay = findFloatingOverlay(target);
  if (!ownOverlay || !focusedOverlay) {
    return true;
  }
  // Only reject when focus is positively in a different, non-nested overlay.
  return (
    ownOverlay === focusedOverlay ||
    ownOverlay.contains(focusedOverlay) ||
    focusedOverlay.contains(ownOverlay)
  );
}

/**
 * Keeps a row's inline editor focused and open while its picker popover
 * (stage, association, select, date…) settles, and treats a genuine popover
 * dismissal as the user's signal that they're done editing.
 *
 * Three cooperating effects:
 *  1. Move focus into the editor when editing starts. Without this the
 *     active element stays wherever it was (often <body> after the trigger
 *     unmounts), so typing needs a second click and Escape reaches the
 *     surrounding slideout — closing it instead of discarding the edit.
 *     Skipped when focus already landed inside. A picker that auto-opens its
 *     (portaled) popover still gets its TRIGGER focused here: react-aria
 *     moves focus into the menu only once the popover has entered (a few
 *     hundred ms), and in that window an Escape from <body> would close the
 *     slideout — from the trigger it reaches the container's own handler
 *     and discards the edit instead.
 *  2. …and keep it there while the editor settles. Two ways focus falls to
 *     <body> mid-edit through no act of the user:
 *      - An auto-opened picker's popover mounts before the effect above runs
 *        (child effects first), so its FocusScope records <body> as the
 *        element to restore to; the popover then remounts once its list is
 *        ready and dutifully "restores" focus to <body>, ~300ms before the
 *        new instance focuses its menu. That one fires `focusout` with no
 *        relatedTarget.
 *      - A multi-value editor re-renders its tags as their objects resolve;
 *        a focused element that is REMOVED loses focus with no event at all,
 *        so that case is caught by watching the editor's subtree instead.
 *     An Escape in either gap reaches the slideout and closes it. Whenever
 *     focus is on <body> mid-edit, put it back on the editor. Skipped once a
 *     commit is in flight (`isSaving`): a click-away that started the save has
 *     intentionally moved focus to the clicked page target, and because the
 *     save is async this editor stays mounted (a disabled control) until it
 *     resolves — rehoming would steal that focus back. useInlineEdit refocuses
 *     the trigger when the save resolves, so there is nothing to keep here.
 *  3. A picker editor opens its popover on entry, and dismissing that
 *     popover — Escape, click-away — is how the user says they're done with
 *     the field. react-aria consumes that Escape and, because the popover is
 *     portaled, closing it fires no blur on the container, so without this
 *     the row would sit in edit state with its bare trigger and focus
 *     dropped on <body>. Mirrors TaskDueDateField's onPickerOpenChange: a
 *     close is a commit — a no-op close when nothing was picked (which also
 *     hands focus back to the row), the real save otherwise. Skipped when
 *     focus has moved into another floating overlay (the picker's
 *     "Create new …" modal): that overlay is still part of this edit. That
 *     check has to wait for focus to SETTLE: when `aria-expanded` flips,
 *     focus is still on the item inside the closing popover (react-aria
 *     restores it only after the exit), and the create-modal case looks
 *     identical at that instant — so the decision is taken on the next focus
 *     move, with a short fallback for the case where focus never left the
 *     page.
 */
export function usePickerOpenGuard(
  containerRef: RefObject<HTMLDivElement | null>,
  isEditing: boolean,
  isSaving: boolean,
  focusEditor: (container: HTMLElement) => void,
  commit: () => Promise<boolean>
): void {
  useLayoutEffect(() => {
    if (!isEditing) {
      return;
    }
    const container = containerRef.current;
    if (!container || container.contains(document.activeElement)) {
      return;
    }
    if (hasFocusEnteredPopover(container, document.activeElement)) {
      return;
    }
    focusEditor(container);
  }, [isEditing, containerRef, focusEditor]);

  useEffect(() => {
    // Stand down once a commit is in flight: an outside click that started the
    // async save deliberately moved focus to the clicked page target, and the
    // editor stays mounted (disabled) until the save resolves — rehoming here
    // would yank that focus back. See effect 2's note in the block comment.
    if (!isEditing || isSaving) {
      return undefined;
    }
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    const rehome = () => {
      queueMicrotask(() => {
        if (
          !container.isConnected ||
          document.activeElement !== document.body
        ) {
          return;
        }
        if (hasFocusEnteredPopover(container, document.activeElement)) {
          return;
        }
        focusEditor(container);
      });
    };
    const onFocusOut = (e: FocusEvent) => {
      if (e.relatedTarget === null) {
        rehome();
      }
    };
    document.addEventListener('focusout', onFocusOut, true);
    const observer = new MutationObserver(rehome);
    observer.observe(container, { childList: true, subtree: true });
    return () => {
      document.removeEventListener('focusout', onFocusOut, true);
      observer.disconnect();
    };
  }, [isEditing, isSaving, containerRef, focusEditor]);

  useEffect(() => {
    if (!isEditing) {
      return undefined;
    }
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }
    let wasOpen = isPopoverOpenWithin(container);
    // If the popover is already open with focus inside it when this effect
    // installs, the opening `focusin` has already fired and `onOpenFocusIn`
    // will never see it: a `defaultOpen` picker's child effects (its FocusScope
    // autofocus) commit before this parent effect, so on first mount the focus
    // can land inside the popover before the listener exists. Record it as
    // settled now; otherwise an Escape close before the fallback timer fires
    // reads as opening-window flicker and the row sticks in edit mode with a
    // closed picker. Require focus to be inside OUR picker's popover — focus
    // parked in an unrelated overlay while our trigger is transiently open must
    // NOT seed a settle, or the opening/remount close commits the row early.
    let settledOpen = hasFocusEnteredOwnPopover(
      container,
      document.activeElement
    );
    let openTimer: ReturnType<typeof setTimeout> | null = null;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    // Reset whenever a genuinely NEW open cycle starts (see the `!wasOpen &&
    // open` branch below) — only counts re-arms within one continuous open.
    let openSettleAttempts = 0;

    const beginOpenSettle = () => {
      if (openTimer !== null) {
        clearTimeout(openTimer);
      }
      openTimer = setTimeout(() => {
        openTimer = null;
        if (!isPopoverOpenWithin(container)) {
          // Closed again already — the close-transition branch below (or a
          // subsequent open) owns what happens next.
          return;
        }
        if (hasFocusEnteredOwnPopover(container, document.activeElement)) {
          settledOpen = true;
          return;
        }
        // Still open, but focus hasn't genuinely landed in OUR popover yet —
        // don't declare settled from the aria-expanded snapshot alone; that
        // stale-open state is what let the fallback fire mid-opening on a
        // slow fetch. Keep waiting, capped so a popover with no focusable
        // content (nothing for focus to ever enter) still settles.
        openSettleAttempts += 1;
        if (openSettleAttempts >= MAX_OPEN_SETTLE_ATTEMPTS) {
          settledOpen = true;
          return;
        }
        beginOpenSettle();
      }, POPOVER_OPEN_SETTLE_MS);
    };
    // A real focusin inside the picker's portaled popover = it has opened.
    // Gate on an actually-open popover: focus entering an unrelated floating
    // overlay (a different picker, a menu elsewhere) while ours is closed must
    // not arm the close watchdog.
    const onOpenFocusIn = (e: FocusEvent) => {
      const t = e.target as Node | null;
      if (t && hasFocusEnteredOwnPopover(container, t)) {
        settledOpen = true;
      }
    };

    const settle = () => {
      document.removeEventListener('focusin', settle, true);
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      // Reopened in the meantime (a remount, a second click) — not a close.
      if (isPopoverOpenWithin(container)) {
        return;
      }
      const active = document.activeElement;
      if (active && isInsideFloatingOverlay(active, { surface: container })) {
        return;
      }
      void commit();
    };

    const observer = new MutationObserver(() => {
      const open = isPopoverOpenWithin(container);
      if (!wasOpen && open) {
        openSettleAttempts = 0;
        beginOpenSettle();
      } else if (wasOpen && !open) {
        if (settledOpen) {
          settledOpen = false;
          document.addEventListener('focusin', settle, true);
          fallbackTimer = setTimeout(settle, POPOVER_CLOSE_SETTLE_MS);
        }
        // else: opening-window flicker — ignore, wait for a genuine open.
      }
      wasOpen = open;
    });
    observer.observe(container, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded'],
    });
    document.addEventListener('focusin', onOpenFocusIn, true);
    if (wasOpen && !settledOpen) {
      beginOpenSettle();
    }
    return () => {
      observer.disconnect();
      document.removeEventListener('focusin', settle, true);
      document.removeEventListener('focusin', onOpenFocusIn, true);
      if (openTimer !== null) {
        clearTimeout(openTimer);
      }
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
      }
    };
  }, [isEditing, commit, containerRef]);
}
